// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Agents terminals' emulators, kept alive off screen (as T3 keeps its terminal drawers).
// A terminal's emulator (xterm.js), its output stream and the machine's connection outlive the panel
// that shows it: switching chats or terminals, leaving Agents or closing the phone sheet only takes
// its element out of the page. Coming back puts the same element back, with everything that came in
// meanwhile already on it: no new emulator, no snapshot to replay, no reconnect.
//
// Off screen a terminal keeps running for KEEP_MS; at most KEEP_MAX are kept (the bridge follows 12
// per connection), the one unseen longest goes first.
//
// It draws with xterm's DOM renderer: the WebGL one paints a transparent background black, and the
// panel's (glass included) shows through the terminal.

import AgentTerminalSessions from '@app/features/konvo_agents/state/AgentTerminalSessions';
import type {AgentTerminalSize} from '@konvo/schema/src/domains/konvo/AgentTerminals';
import {FitAddon} from '@xterm/addon-fit';
import {type ITheme, Terminal} from '@xterm/xterm';

/** How long a terminal off screen keeps its emulator and stream. */
const KEEP_MS = 10 * 60_000;
/** Terminals kept at once, on screen or off. */
const KEEP_MAX = 8;

export interface TerminalViewTarget {
	machineId: string;
	threadId: string;
	id: string;
	/** Opened from Konvo: it follows the panel's size (T3's keep their own width). */
	own: boolean;
}

/** What the panel showing a terminal tells it. */
export interface TerminalScreen {
	/** The panel's frame: the theme and font are read from it, the emulator fits it. */
	frame: HTMLElement;
	/** The class of the element the emulator draws in. */
	hostClass: string;
	phone: boolean;
	/** Offline or ended: typing does nothing. */
	disabled: boolean;
	/** Changes what was typed (the phone's sticky Ctrl), else sends it as it is. */
	typed?: (data: string) => string;
}

export interface TerminalView {
	readonly key: string;
	readonly target: TerminalViewTarget;
	readonly term: Terminal;
	/** What the emulator was opened in; moves from panel to panel. */
	readonly element: HTMLDivElement;
	/** Fits the panel showing it (nothing while off screen). */
	layout(): void;
}

interface Kept extends TerminalView {
	fit: FitAddon;
	screen: TerminalScreen | null;
	t3Size: AgentTerminalSize | null;
	hiddenAt: number;
	timer: ReturnType<typeof setTimeout> | null;
	dispose(): void;
}

const keyOf = ({machineId, threadId, id}: TerminalViewTarget) => `${machineId}\u0000${threadId}\u0000${id}`;

/** Konvo's colours for the emulator, read from the panel's CSS variables in the current theme. */
export function themeFrom(el: HTMLElement): ITheme {
	const probe = document.createElement('span');
	probe.style.display = 'none';
	el.appendChild(probe);
	const canvas = document.createElement('canvas').getContext('2d');
	const read = (value: string, fallback: string) => {
		probe.style.color = fallback;
		probe.style.color = value;
		const css = getComputedStyle(probe).color || fallback;
		if (!canvas) return css;
		// color-mix() and friends become plain rgb() the emulator understands.
		canvas.clearRect(0, 0, 1, 1);
		canvas.fillStyle = '#000';
		canvas.fillStyle = css;
		canvas.fillRect(0, 0, 1, 1);
		const [r, g, b, a] = canvas.getImageData(0, 0, 1, 1).data;
		return a === 255 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${(a! / 255).toFixed(3)})`;
	};
	const theme: ITheme = {
		background: 'rgba(0, 0, 0, 0)',
		foreground: read('var(--text-primary)', '#222'),
		cursor: read('var(--text-primary)', '#222'),
		cursorAccent: read('var(--tk-term-solid)', '#fff'),
		selectionBackground: read('var(--tk-select)', 'rgba(80, 120, 200, 0.3)'),
		black: read('var(--tk-k)', '#333'),
		brightBlack: read('var(--tk-kb)', '#666'),
		red: read('var(--tk-r)', '#c33'),
		brightRed: read('var(--tk-r)', '#c33'),
		green: read('var(--tk-g)', '#2a2'),
		brightGreen: read('var(--tk-g)', '#2a2'),
		yellow: read('var(--tk-y)', '#a80'),
		brightYellow: read('var(--tk-y)', '#a80'),
		blue: read('var(--tk-b)', '#36c'),
		brightBlue: read('var(--tk-b)', '#36c'),
		magenta: read('var(--tk-m)', '#94b'),
		brightMagenta: read('var(--tk-m)', '#94b'),
		cyan: read('var(--tk-c)', '#088'),
		brightCyan: read('var(--tk-c)', '#088'),
		white: read('var(--tk-w)', '#bbb'),
		brightWhite: read('var(--tk-wb)', '#eee'),
		scrollbarSliderBackground: read('var(--tk-soft2)', 'rgba(0,0,0,0.1)'),
		scrollbarSliderHoverBackground: read('var(--tk-soft2)', 'rgba(0,0,0,0.15)'),
		scrollbarSliderActiveBackground: read('var(--tk-soft2)', 'rgba(0,0,0,0.2)'),
	};
	probe.remove();
	return theme;
}

/** The app's mono font as a plain list (the emulator measures its cells with it, so no var()). */
export function monoFont(el: HTMLElement): string {
	const probe = document.createElement('span');
	probe.style.display = 'none';
	probe.style.fontFamily = "var(--font-mono), 'IBM Plex Mono', ui-monospace, Menlo, Consolas, monospace";
	el.appendChild(probe);
	const family = getComputedStyle(probe).fontFamily;
	probe.remove();
	return family && !family.includes('var(') ? family : "'IBM Plex Mono', ui-monospace, Menlo, Consolas, monospace";
}

const isMac = () => /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

function copyText(text: string) {
	if (text) void navigator.clipboard?.writeText(text).catch(() => undefined);
}

/** All the text the emulator holds (scrollback and screen), as plain lines. */
export function bufferText(term: Terminal): string {
	const buffer = term.buffer.active;
	const lines: Array<string> = [];
	for (let i = 0; i < buffer.length; i++) lines.push(buffer.getLine(i)?.translateToString(true) ?? '');
	while (lines.length && !lines.at(-1)) lines.pop();
	return lines.join('\n');
}

class AgentTerminalViewsStore {
	private readonly views = new Map<string, Kept>();

	/** Puts the terminal's emulator in the panel's frame (made the first time, else the one kept). */
	show(target: TerminalViewTarget, screen: TerminalScreen): TerminalView {
		const host = screen.frame;
		const key = keyOf(target);
		let view = this.views.get(key);
		if (view && view.target.own !== target.own) {
			view.dispose();
			view = undefined;
		}
		if (view?.timer) clearTimeout(view.timer);
		const fresh = !view;
		view ??= this.make(key, target, screen);
		view.timer = null;
		view.screen = screen;
		if (view.element.parentElement !== host) host.appendChild(view.element);
		const term = view.term;
		term.options.fontSize = screen.phone ? 12 : 13;
		term.options.disableStdin = screen.disabled;
		if (!fresh) {
			// The theme or font may have changed while it was off screen.
			term.options.theme = themeFrom(screen.frame);
			term.options.fontFamily = monoFont(screen.frame);
		}
		view.layout();
		if (!fresh) term.refresh(0, term.rows - 1);
		this.prune();
		return view;
	}

	/** The panel no longer shows it: out of the page, kept running for a while. */
	hide(view: TerminalView, screen: TerminalScreen) {
		const kept = this.views.get(view.key);
		if (kept !== view || kept.screen !== screen) return;
		kept.screen = null;
		kept.hiddenAt = Date.now();
		kept.element.remove();
		kept.timer = setTimeout(() => kept.dispose(), KEEP_MS);
		this.prune();
	}

	/** The terminal is gone (closed from Konvo): its emulator goes too. */
	drop(target: TerminalViewTarget) {
		this.views.get(keyOf(target))?.dispose();
	}

	private make(key: string, target: TerminalViewTarget, first: TerminalScreen): Kept {
		const {machineId, threadId, id, own} = target;
		const element = document.createElement('div');
		element.className = first.hostClass;
		first.frame.appendChild(element);
		const term = new Terminal({
			fontFamily: monoFont(first.frame),
			fontSize: first.phone ? 12 : 13,
			lineHeight: 1.2,
			cursorBlink: true,
			allowTransparency: true,
			scrollback: 5000,
			macOptionIsMeta: true,
			theme: themeFrom(first.frame),
			// Links do nothing: nothing in a terminal may act on Konvo.
			linkHandler: {activate: () => {}},
		});
		// OSC 52 (clipboard) and OSC 8 (links) are swallowed here too, whatever the machine sent.
		term.parser.registerOscHandler(52, () => true);
		term.parser.registerOscHandler(8, () => true);
		const fit = new FitAddon();
		term.loadAddon(fit);
		term.open(element);

		// The machine's connection stays while the emulator does (the panel's own use comes and goes).
		const release = AgentTerminalSessions.use(machineId, threadId);
		const view: Kept = {
			key,
			target,
			term,
			element,
			fit,
			screen: null,
			t3Size: own ? null : AgentTerminalSessions.sizeOf(machineId, threadId, id),
			hiddenAt: 0,
			timer: null,
			layout: () => {
				if (!view.screen) return;
				const proposed = fit.proposeDimensions();
				if (!proposed || !Number.isFinite(proposed.cols) || !Number.isFinite(proposed.rows)) return;
				const rows = Math.max(4, proposed.rows);
				if (own) {
					const cols = Math.max(20, proposed.cols);
					if (term.cols !== cols || term.rows !== rows) term.resize(cols, rows);
					AgentTerminalSessions.resize(machineId, threadId, id, cols, rows);
					element.style.width = '';
				} else {
					const cols = view.t3Size?.cols ?? Math.max(20, proposed.cols);
					if (term.cols !== cols || term.rows !== rows) term.resize(cols, rows);
					// As wide as T3's terminal: the frame scrolls sideways when the panel is narrower.
					const screenEl = element.querySelector<HTMLElement>('.xterm-screen');
					element.style.width = screenEl?.style.width ? `calc(${screenEl.style.width} + 12px)` : '';
				}
			},
			dispose: () => {
				if (this.views.get(key) !== view) return;
				this.views.delete(key);
				if (view.timer) clearTimeout(view.timer);
				data.dispose();
				detach();
				release();
				term.dispose();
				element.remove();
			},
		};

		const data = term.onData((input) => {
			const screen = view.screen;
			if (!screen || screen.disabled) return;
			AgentTerminalSessions.input(machineId, threadId, id, screen.typed ? screen.typed(input) : input);
		});
		term.attachCustomKeyEventHandler((event) => {
			if (event.type !== 'keydown') return true;
			const k = event.key.toLowerCase();
			// Copy what is selected (Ctrl-C with nothing selected stays Ctrl-C, the interrupt).
			if ((event.ctrlKey || event.metaKey) && k === 'c' && term.hasSelection()) {
				copyText(term.getSelection());
				return false;
			}
			if (event.ctrlKey && event.shiftKey && k === 'c') {
				copyText(term.getSelection());
				return false;
			}
			// Paste goes through the browser's paste (the emulator takes it from there).
			if (event.ctrlKey && k === 'v' && (event.shiftKey || !isMac())) return false;
			return true;
		});
		// The app's own shortcuts stay out of the way while typing here.
		element.addEventListener('keydown', (event) => event.stopPropagation());
		// Output goes to the emulator whether it is on screen or not: coming back shows it as it is now.
		const detach = AgentTerminalSessions.attach(machineId, threadId, id, {
			snapshot: (history, size) => {
				if (!own && size) view.t3Size = size;
				term.reset();
				view.layout();
				term.write(history);
			},
			output: (chunk) => term.write(chunk),
			size: (size) => {
				if (own) return;
				view.t3Size = size;
				view.layout();
			},
		});
		this.views.set(key, view);
		return view;
	}

	/** At most KEEP_MAX: the terminals off screen longest go first. */
	private prune() {
		const hidden = [...this.views.values()].filter((v) => !v.screen).sort((a, b) => a.hiddenAt - b.hiddenAt);
		let over = this.views.size - KEEP_MAX;
		for (const view of hidden) {
			if (over-- <= 0) break;
			view.dispose();
		}
	}
}

export default new AgentTerminalViewsStore();
